import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import TopNavbar from "@/components/layout/TopNavbar";
import Footer from "@/components/home/Footer";

const OurWhy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Our Why - The Hivetivity Story | Built by Owners, for Owners</title>
        <meta
          name="description"
          content="Meet Ryan and Vanessa Hall, the husband-and-wife team behind Hivetivity. Learn why we built a platform that truly understands the real-life challenges of activity center owners."
        />
        <meta
          name="keywords"
          content="hivetivity story, about hivetivity, ryan and vanessa hall, activity center software story"
        />
        <link rel="canonical" href="https://hivetivity.com/our-why" />
      </Helmet>

      <TopNavbar />

      <div className="pt-16">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              The Hivetivity Story
            </h1>
            {/* Hive pattern background */}
            <div className="relative rounded-2xl overflow-hidden">
              {/* Hive pattern CSS background */}
              <div
                className="absolute inset-0 z-0 pointer-events-none"
                style={{
                  backgroundImage: "url('https://media.hivetivity.com/download.svg')",
                  backgroundRepeat: "repeat",
                  backgroundSize: "120px 120px",
                  opacity: 0.7,
                  mixBlendMode: "multiply",
                }}
              />
              <div className="relative z-10 space-y-6 text-lg leading-relaxed text-gray-700 text-left p-6 sm:p-10">
                <p>
                  Hi, we're Ryan and Vanessa Hall — a husband-and-wife team with over 40 years of combined software and business experience. We believe that running a kids' activity business should feel energizing, not exhausting.
                </p>
                <p>
                  After decades in tech and years of seeing friends struggle with complicated, overpriced tools, we set out to build something better — a platform that truly understands the real-life challenges of activity center owners.
                </p>
                <p>
                  <strong>Human-centered at every click:</strong> We obsess over details so you don't have to. Hivetivity is designed to be welcoming, intuitive, and friendly from the very first login — no tech degree required.
                </p>
                <p>
                  <strong>Built on real expertise:</strong> Behind the scenes, Hivetivity runs on decades of software know-how and best-in-class technology. You get rock-solid reliability, enterprise-grade security, and a platform that's built to scale as you grow.
                </p>
                <p>
                  <strong>Advanced, but effortless:</strong> Sure, we nailed the basics (rosters, payments, forms), but our platform quietly runs on state-of-the-art architecture — so you get enterprise-grade power without enterprise-grade headaches.
                </p>
                <p>
                  <strong>Genuine time-savers:</strong> Features like instant document management, drag-and-drop form design, built-in e-signature, and our "Ask AI" tool aren't just bells and whistles — they're here to win back your nights and weekends.
                </p>
                <p>
                  <strong>Grow your business, not your to-do list:</strong> Hivetivity lets parents enroll right from your website, manages waitlists with auto-notifications, handles two-way messaging, and automates billing — so you can spend less time on admin, and more time building your program.
                </p>
                <div className="py-8">
                  <Card className="overflow-hidden shadow-sm border border-gray-200">
                    <img
                      src="https://media.hivetivity.com/Hivetivity%20Our%20Why.avif"
                      alt="Ryan and Vanessa Hall, founders of Hivetivity, smiling together at their family table"
                      className="w-full h-full object-cover"
                    />
                  </Card>
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                  For us, "Hive" isn't just a name — it's a promise.
                </h2>
                <p>
                  A hive is a place where everyone belongs, everyone contributes, and together, the whole is greater than the sum of its parts. That's how we see our community — a thriving, buzzing place where owners, families, and kids all support each other, and where great technology just gets out of your way.
                </p>
                <p>
                  We use Hivetivity with our own family, and we stand by every decision in the product. When you reach out, you're talking to us — not a faceless support bot.
                </p>
                {/* Octagon divider before the closing section */}
                <div className="flex justify-center my-10">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <polygon points="24,4 44,13 44,35 24,44 4,35 4,13" stroke="#FA8C00" strokeWidth="3" fill="none" />
                  </svg>
                </div>
                <div className="text-center mt-8">
                  <p className="text-xl font-semibold text-[#FA8C00] mb-2">
                    Welcome to Hivetivity.
                  </p>
                  <p className="text-lg text-gray-600 italic" style={{ fontFamily: 'Georgia, serif' }}>
                    Built by parents, powered by decades of experience.<br />
                    Tools as passionate and professional as you are.<br /><br />
                    — Ryan & Vanessa
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12 pt-8 border-t border-gray-200">
                  <Button
                    onClick={() => navigate("/business-trial")}
                    size="lg"
                    className="bg-[#FA8C00] hover:bg-[#FA8C00]/90 text-white px-8 py-3"
                  >
                    Join the Hive
                  </Button>
                  <Button
                    onClick={() => navigate("/contact")}
                    size="lg"
                    variant="outline"
                    className="border-2 border-gray-300 hover:border-[#FA8C00] hover:text-[#FA8C00] px-8 py-3"
                  >
                    Let's Chat
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OurWhy;